import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Typography,
  Box,
  Fab,
  Paper,
  Divider,
  Chip
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';
import useKanbanAutomations from '../../hooks/useKanbanAutomations';
import KanbanRuleBuilder from './KanbanRuleBuilder';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  automationsList: {
    maxHeight: 400,
    overflow: 'auto',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  automationItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  automationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  automationDetails: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
  },
  triggerChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  actionChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
  },
  fab: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  rulePreview: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

const KanbanAutomationModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { 
    automations, 
    loading, 
    error,
    fetchAutomations,
    createAutomation, 
    updateAutomation, 
    deleteAutomation, 
    toggleAutomation,
    testAutomation,
    validateRule 
  } = useKanbanAutomations();
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);

  useEffect(() => {
    if (open) {
      fetchAutomations();
    }
  }, [open, fetchAutomations]);

  const handleToggleAutomation = async (automationId, currentActive) => {
    try {
      await toggleAutomation(automationId, !currentActive);
    } catch (error) {
      console.error('Error toggling automation:', error);
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    if (window.confirm(i18n.t('kanban.confirmDelete'))) {
      try {
        await deleteAutomation(automationId);
      } catch (error) {
        console.error('Error deleting automation:', error);
      }
    }
  };

  const handleEditAutomation = (automation) => {
    setSelectedAutomation(automation);
    setShowRuleBuilder(true);
  };

  const handleCreateNew = () => {
    setSelectedAutomation(null);
    setShowRuleBuilder(true);
  };
  
  const handleTestAutomation = async (automationId) => {
    try {
      await testAutomation(automationId);
    } catch (error) {
      console.error('Error testing automation:', error);
    }
  };
  
  const handleSaveAutomation = async (automationData) => {
    try {
      const validation = validateRule(automationData);
      if (!validation.isValid) {
        // Mostrar erros de validação
        validation.errors.forEach(error => {
          console.error('Validation error:', error);
        });
        return;
      }
      
      if (selectedAutomation) {
        await updateAutomation(selectedAutomation.id, automationData);
      } else {
        await createAutomation(automationData);
      }
      
      setShowRuleBuilder(false);
      setSelectedAutomation(null);
    } catch (error) {
      console.error('Error saving automation:', error);
    }
  };

  const formatTrigger = (triggerType) => {
    if (!triggerType) return 'Trigger indefinido';
    return i18n.t(`kanban.automation.triggerTypes.${triggerType}`) || triggerType;
  };

  const formatAction = (actions) => {
    if (!actions || !Array.isArray(actions) || actions.length === 0) return 'Ação indefinida';
    const firstAction = actions[0];
    return i18n.t(`kanban.automation.actionTypes.${firstAction.type}`) || firstAction.type;
  };
  
  const getLastRunText = (lastRun) => {
    if (!lastRun) return i18n.t('kanban.neverRun');
    return new Date(lastRun).toLocaleString();
  };

  const renderAutomationList = () => {
    if (loading) {
      return (
        <Box className={classes.emptyState}>
          <Typography>{i18n.t('kanban.loading')}</Typography>
        </Box>
      );
    }

    if (!automations.length) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="h6">{i18n.t('kanban.automation.noAutomations')}</Typography>
          <Typography>{i18n.t('kanban.automation.getStarted')}</Typography>
        </Box>
      );
    }

    return (
      <List className={classes.automationsList}>
        {automations.map((automation) => (
          <Paper key={automation.id} className={classes.automationItem} elevation={1}>
            <div className={classes.automationHeader}>
              <div style={{ flex: 1 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                  {automation.name}
                </Typography>
                <Typography variant="body2" className={classes.rulePreview}>
                  {i18n.t('kanban.automation.when')} {formatTrigger(automation.triggerType)} →{' '}
                  {i18n.t('kanban.automation.then')} {formatAction(automation.actions)}
                </Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={automation.active}
                  onChange={() => handleToggleAutomation(automation.id, automation.active)}
                  color="primary"
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => handleEditAutomation(automation)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleTestAutomation(automation.id)}
                  title="Testar Automação"
                  disabled={!automation.active}
                >
                  <PlayIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteAutomation(automation.id)}
                  color="secondary"
                  title="Excluir Automação"
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </div>
            
            <Divider />
            
            <div className={classes.automationDetails}>
              <Typography variant="caption" display="block" gutterBottom>
                {i18n.t('kanban.automation.triggers')}:
              </Typography>
              <Chip
                size="small"
                label={formatTrigger(automation.triggerType)}
                className={classes.triggerChip}
              />

              <Typography variant="caption" display="block" gutterBottom style={{ marginTop: 8 }}>
                {i18n.t('kanban.automation.actions')}:
              </Typography>
              {automation.actions && automation.actions.map((action, index) => (
                <Chip
                  key={index}
                  size="small"
                  label={i18n.t(`kanban.automation.actionTypes.${action.type}`) || action.type}
                  className={classes.actionChip}
                  style={{ marginRight: 4 }}
                />
              ))}
              
              <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                {i18n.t('kanban.automation.lastRun')}: {
                  automation.lastExecutedAt
                    ? new Date(automation.lastExecutedAt).toLocaleString()
                    : i18n.t('kanban.automation.neverRun')
                }
              </Typography>
              <Typography variant="caption" display="block">
                Execuções: {automation.executionCount || 0}
              </Typography>
            </div>
          </Paper>
        ))}
      </List>
    );
  };

  if (showRuleBuilder) {
    return (
      <KanbanRuleBuilder
        open={showRuleBuilder}
        onClose={() => {
          setShowRuleBuilder(false);
          setSelectedAutomation(null);
        }}
        automation={selectedAutomation}
        onSave={() => {
          setShowRuleBuilder(false);
          setSelectedAutomation(null);
          fetchAutomations(); // Refresh the list
        }}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{i18n.t('kanban.automation.title')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.root}>
        {renderAutomationList()}
        
        <Fab
          color="primary"
          aria-label="add"
          className={classes.fab}
          onClick={handleCreateNew}
        >
          <AddIcon />
        </Fab>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {i18n.t('kanban.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanAutomationModal;